import { toast } from '@wellesley/system-toast'
import ConfirmModal from 'components/modals/ConfirmModal'
import { isBlocked } from '@wellesley/user/helpers/relationship'
import {
  User,
  Relationship,
  usePostApiV1BlockByUserIdMutation,
  useDeleteApiV1BlockByUserIdMutation,
} from '@wellesley/openapi'

import { useSwitchState } from '../useSwitchState'
import { getErrorMsg } from '@wellesley/error-kit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

type HookProps = {
  user: User
  relationship: Relationship
  showConfirmationDialog?: boolean
  onBlocked?: () => void
}

const useBlock = ({
  user,
  relationship,
  showConfirmationDialog = true,
  onBlocked,
}: HookProps) => {
  const [handleBlock, { isLoading: isBlocking }] =
    usePostApiV1BlockByUserIdMutation()
  const [handleUnblock] = useDeleteApiV1BlockByUserIdMutation()

  const [isBlockDialogOpen, openDialog, closeDialog] = useSwitchState()

  const isUserBlocked = isBlocked(relationship)

  const onBlock = async () => {
    try {
      await handleBlock({ userId: user.id }).unwrap()
      toast.success(`${user.username} is blocked`, {
        dataTestId: 'toast-block-success',
      })
      closeDialog()
      onBlocked?.()
    } catch (error) {
      toast.error(`${getErrorMsg(error as FetchBaseQueryError)}`)
    }
  }

  const unblock = async () => {
    try {
      await handleUnblock({ userId: user.id }).unwrap()
      toast.success(`${user.username} is unblocked`, {
        dataTestId: 'toast-unblock-success',
      })
    } catch (error) {
      toast.error(`${getErrorMsg(error as FetchBaseQueryError)}`)
    }
  }

  const renderBlockDialog = () => {
    return (
      <ConfirmModal
        open={isBlockDialogOpen}
        title={`Do you want to block ${user.username}?`}
        onClose={closeDialog}
        data-testid="block-dialog"
        leftButton={{
          title: 'Cancel',
          isLoading: false,
          variant: 'secondary',
          onClick: closeDialog,
        }}
        rightButton={{
          title: 'Block',
          variant: 'red_primary',
          isLoading: isBlocking,
          onClick: () => void onBlock(),
        }}
      >
        Blocked users will not be able to follow you or see your content, and
        you will not see any content from them. They will also be aware that
        they have been blocked.
      </ConfirmModal>
    )
  }

  return [
    isUserBlocked,
    showConfirmationDialog ? openDialog : onBlock,
    unblock,
    renderBlockDialog,
    isBlockDialogOpen,
  ] as const
}

export default useBlock
